/**
 * Demolition-scope predicates + the auto-pull collector. Pure: no React,
 * no canvas, no writes. A single orthogonal `demoed: true` field marks an
 * object/opening for demolition. Walls keep their own demoed-opening
 * representation (category.opening.demoed) and are NOT covered here.
 */
import {
  isOpeningWithinWallSpan,
  getObjectsOnWall,
} from './elementDrawingUtils';
import { isDemoedOpening } from './openingSyncUtils';
import { getObjectType } from './objectTypeUtils';

export const isDemoed = (el) => el?.demoed === true;

const WALL_DEPENDENT_FIXTURE_TYPES = [
  'wall-light',
  'sconce',
  'mirror',
  'switch',
  'outlet',
  'art',
  'television',
];

const FLOOR_STANDING_TYPES = [
  'cabinet',
  'vanity',
  'counter',
  'countertop',
  'appliance',
  'toilet',
  'sink',
  'island',
  'peninsula',
  'bath',
  'stove',
  'range',
  'cooktop',
  'oven',
  'dishwasher',
  'refrigerator',
  'hood',
];

const matchesType = (type, list) =>
  !!type && list.some((t) => type === t || type.includes(t));

export const isWallDependent = (el) => {
  if (!el) return false;
  if (isDemoedOpening(el)) return false; // the marker IS the demolition
  const cat = typeof el.category === 'object' && el.category ? el.category : {};
  if (cat.door || cat.window || cat.opening) return true;
  const type = getObjectType(el);
  if (type === 'door' || type === 'window' || type === 'opening') return true;
  if (matchesType(type, FLOOR_STANDING_TYPES)) return false; // exclusion wins over loose fixture match
  return matchesType(type, WALL_DEPENDENT_FIXTURE_TYPES);
};

export const collectAutoDemoedElements = (demoedSpan, rooms) => {
  if (!demoedSpan?.center || !demoedSpan?.dimensions) return [];
  const roomList = rooms instanceof Map ? Array.from(rooms.values()) : rooms;
  if (!Array.isArray(roomList)) return [];
  const result = [];
  for (const room of roomList) {
    if (!room?.id) continue;
    for (const arrayName of ['doors', 'windows', 'openings']) {
      const arr = room[arrayName];
      if (!Array.isArray(arr)) continue;
      for (const el of arr) {
        if (!el?.id) continue;
        if (isDemoed(el)) continue;
        if (!isWallDependent(el)) continue;
        if (!isOpeningWithinWallSpan(el, demoedSpan)) continue;
        result.push({ roomId: room.id, arrayName, elementId: el.id });
      }
    }
    const objects = Array.isArray(room.objects) ? room.objects : [];
    const candidates = getObjectsOnWall(demoedSpan, objects);
    for (const el of candidates) {
      if (!el?.id) continue;
      if (isDemoed(el)) continue;
      if (!isWallDependent(el)) continue;
      result.push({ roomId: room.id, arrayName: 'objects', elementId: el.id });
    }
  }
  return result;
};

/**
 * Structure ids of the openings marked for demolition. The mark is set on an
 * opening's ROOM copies (`collectAutoDemoedElements` flags the room arrays),
 * so a reader of the structure document — the placement overlap check, which
 * lets a new opening replace a demolished one — has to collect it here.
 *
 * @param {Map|Array} rooms - the room store (Map) or a room list
 * @returns {Set<string>}
 */
export const demoedStructureOpeningIds = (rooms) => {
  const ids = new Set();
  const roomList = rooms instanceof Map ? Array.from(rooms.values()) : rooms;
  for (const room of Array.isArray(roomList) ? roomList : []) {
    for (const arrayName of ['doors', 'windows', 'openings']) {
      for (const el of room?.[arrayName] || []) {
        if (isDemoed(el) && el.structureOpeningId) {
          ids.add(el.structureOpeningId);
        }
      }
    }
  }
  return ids;
};
